import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { playOverwhelmChime } from "@/lib/overwhelmSound";
import { consumeIfMatchesCompletion } from "@/lib/mochiPick";

// Overwhelm Panic flow.
// `overwhelmOpen` is the bottom-sheet visibility. `dimWorld` triggers the 20%
// fade of the world behind it (separate from `open` because the dim starts
// the instant the button is tapped, before the sheet finishes its 350ms
// slide-up). `inFocusTaskId` floats the user's chosen task to the top of the
// task list with a soft highlight ring. The "armed" ref is what we check on
// task completion to fire the special toast — a ref (not state) so the
// wrapped toggle callback stays stable.
export function useOverwhelmFlow({ mood, handleToggleTask, setMochiPick }) {
    const [overwhelmOpen, setOverwhelmOpen] = useState(false);
    const [dimWorld, setDimWorld] = useState(false);
    const [inFocusTaskId, setInFocusTaskId] = useState(null);
    const overwhelmArmedRef = useRef(null);

    // Sequence (per spec):
    //   1. world dims to 20% immediately (400ms transition via CSS)
    //   2. current mood → worried over 300ms
    //   3. then worried → determined over 500ms
    //   4. soft chime plays
    //   5. sheet slides up over 350ms (handled inside OverwhelmOverlay)
    const openOverwhelm = useCallback(() => {
        playOverwhelmChime();
        setDimWorld(true);
        mood.forceMoodFor?.("worried", 300);
        setTimeout(() => mood.forceMoodFor?.("determined", 60_000), 300);
        setTimeout(() => setOverwhelmOpen(true), 60);
    }, [mood]);

    // "i just need a minute" / backdrop tap / swipe-down — all funnel here.
    const dismissOverwhelmGently = useCallback(() => {
        setOverwhelmOpen(false);
        setDimWorld(false);
        mood.forceMoodFor?.("cozy", 5_000);
        mood.setBubbleMsg?.("that's okay too. i'll be here.");
    }, [mood]);

    // "let's start" — pin the picked task as the in-focus item, arm the
    // recovery toast, drop the dim, close the sheet.
    const startOverwhelmTask = useCallback((card) => {
        const sourceId = card?.sourceId;
        if (sourceId && card.kind === "task") {
            setInFocusTaskId(sourceId);
            overwhelmArmedRef.current = sourceId;
        }
        setOverwhelmOpen(false);
        setDimWorld(false);
        mood.setBubbleMsg?.("okay. just that one. everything else can wait.");
    }, [mood]);

    // Wraps `handleToggleTask` so a completion of the armed task triggers
    // the special recovery toast + Mochi proud→happy chain, and pings the
    // backend stat. Also consumes Mochi's pick when the completed task
    // matches it. Non-armed completions pass through unchanged.
    const handleToggleTaskWithOverwhelm = useCallback(async (t) => {
        const armed = overwhelmArmedRef.current === t.id && !t.completed;
        const completing = !t.completed;
        await handleToggleTask(t);
        if (completing) {
            const result = consumeIfMatchesCompletion(t.id);
            if (result === "special") {
                setMochiPick(null);
                toast("that's exactly what i thought you'd do 🍡", { duration: 4500 });
                mood.forceMoodFor?.("proud", 2500);
            } else if (result === "expired") {
                // Pick was for this task but the 30-min window elapsed — quietly clear.
                setMochiPick(null);
            }
        }
        if (armed) {
            overwhelmArmedRef.current = null;
            setInFocusTaskId(null);
            toast("you did it. that took courage. 🍡", { duration: 4500 });
            mood.forceMoodFor?.("proud", 2500);
            try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/overwhelm-recovery`, { method: "POST", credentials: "include" });
            } catch (e) {
                console.warn("overwhelm: recovery stat ping failed", e);
            }
        }
    }, [handleToggleTask, mood, setMochiPick]);

    return {
        overwhelmOpen, dimWorld, inFocusTaskId,
        openOverwhelm, dismissOverwhelmGently, startOverwhelmTask,
        handleToggleTaskWithOverwhelm,
    };
}
